import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { useSettings } from "../lib/settings";

/**
 * The price question names a figure, and the price is set from the dashboard.
 * Hard-coding it would put a stale number a few sections below the live one in
 * the pricing card — the two would contradict each other on the same page the
 * first time somebody changes it.
 *
 * The literal below is only what shows in the moment before settings load; the
 * configured price replaces it as soon as it arrives.
 */
const FALLBACK_PRICE_LABEL = "975 lei";

const priceLabel = (price, currency) =>
  price === null || price === undefined
    ? FALLBACK_PRICE_LABEL
    : `${Number(price).toLocaleString("ro-RO")} ${currency === "RON" ? "lei" : currency}`;

const faqItems = (price, currency) => [
  {
    q: "Cum plătesc cu cardul Prima de carieră didactică?",
    a: "Apeși butonul Înscrie-te acum, completezi datele de facturare și plătești cu cardul tău Prima ca la orice magazin online. Banii sunt debitați direct de pe card, iar tu primești factură și acces la curs.",
  },
  {
    q: "Cursul este acreditat?",
    a: "Da. Programul este acreditat prin OMEC nr. 4712/19.08.2026, ca program complementar de formare continuă pentru personalul didactic.",
  },
  {
    q: "Cum se desfășoară cursul?",
    a: "Cursul are 25 de ore: 15 ore live cu instructor, desfășurate online în format meeting (teorie, demonstrații și exemple practice), plus 10 ore de muncă individuală pentru aplicarea practică a noțiunilor învățate. Sesiunile live se țin pe Zoom / Google Meet și pot fi revizuite ulterior pe platformă.",
  },
  {
    q: "Cu ce rămân după finalizarea cursului?",
    a: "Pe parcursul programului vei lucra pe exemple și scenarii practice aplicabile la clasă. Vei descoperi metode de utilizare AI pentru pregătirea lecțiilor, evaluare, feedback și organizarea activităților didactice, folosind resurse pe care le poți adapta ulterior în funcție de disciplina și nivelul elevilor.",
  },
  {
    q: "Cât durează cursul?",
    a: "Cursul are o durată totală de 25 de ore. Dintre acestea, 15 ore sunt sesiuni live cu instructor, desfășurate online în format meeting, iar 10 ore sunt dedicate muncii individuale și aplicării practice a noțiunilor învățate.",
  },
  {
    q: "Primesc certificat?",
    a: "Da. La finalizarea celor 10 module și a proiectului final, primești certificat de absolvire, emis în baza acreditării OMEC nr. 4712/19.08.2026. Cursul oferă 1 credit ECTS la absolvire.",
  },
  {
    q: "Pot solicita rambursare?",
    a: "Conform legislației în vigoare, ai drept de retragere în primele 14 zile dacă nu ai accesat o parte semnificativă a conținutului. Pentru plățile făcute cu cardul Prima de carieră didactică se aplică însă condiții speciale, pentru că sumele de pe card au destinație specială prin lege — detaliile sunt în secțiunea 4 din Termeni și Condiții.",
  },
  // The three below answer the objection people raise before they enrol
  // rather than after, which is why they sit at the end: somebody who is
  // still reading the FAQ at this point is weighing the price.
  {
    q: `De ce costă programul ${priceLabel(price, currency)}?`,
    a: "Programul a fost construit astfel încât să ofere o experiență de formare practică, nu doar acces la conținut. Include 15 ore de sesiuni live cu formatorul, 10 ore de activitate individuală și evaluare, materiale de curs și întregul proces aferent unui program acreditat. Am urmărit, în același timp, ca prețul să se încadreze în plafonul maxim eligibil prin programul Prima de Carieră Didactică, astfel încât profesorii eligibili să poată participa fără să fie nevoie de o plată suplimentară din fonduri proprii.",
  },
  {
    q: "De ce poate părea prețul mai mare decât al altor cursuri pentru profesori?",
    a: "În formarea cadrelor didactice există și programe oferite prin instituții publice sau proiecte finanțate din fonduri publice ori europene. În astfel de situații, costul suportat direct de participant poate fi foarte mic sau chiar zero, fără ca acesta să fie însă costul real al formării. Programul nostru este livrat ca un program de formare specializată, cu 15 ore de lucru live cu formatorul, activitate practică, evaluare și materiale dedicate. Din acest motiv, o comparație relevantă este mai degrabă cu alte programe de formare profesională livrate pe piața liberă decât cu un curs al cărui cost este suportat integral sau parțial din alte surse.",
  },
  {
    q: "Este un curs online obișnuit, bazat pe materiale preînregistrate?",
    a: "Nu. Cea mai importantă parte a programului este lucrul live cu formatorul. Din cele 25 de ore, 15 ore sunt sesiuni online interactive, în care participanții lucrează practic cu instrumente AI și exemple din activitatea didactică. Celelalte 10 ore sunt dedicate exercițiului individual, aplicării practice și evaluării. Scopul programului este ca profesorii să plece cu competențe pe care să le poată folosi efectiv în activitatea de zi cu zi, nu doar cu o certificare.",
  },
];

export default function Faq() {
  const { price, currency } = useSettings();
  const items = faqItems(price, currency);

  return (
    <section id="faq" className="scroll-mt-28 py-20 md:py-28 bg-[#EDEEF0]">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center">
          <span className="inline-flex items-center rounded-full bg-white border border-[#E5E7EB] px-3 py-1 text-xs font-semibold text-[#373A45] uppercase tracking-widest">
            Întrebări frecvente
          </span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-[#373A45]">
            Răspundem la cele mai des întâlnite întrebări.
          </h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 bg-white rounded-2xl border border-[#E5E7EB] px-6 md:px-8" data-testid="faq-accordion">
          {items.map((it, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="border-b border-[#E5E7EB] last:border-b-0">
              <AccordionTrigger className="text-left hover:no-underline hover:text-[#965EA7] py-5 font-display text-lg font-semibold text-[#373A45]" data-testid={`faq-trigger-${i}`}>
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#6B7280] leading-relaxed pb-6">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
